import CartAPI from "./CartApi";
import RewardManager from "./RewardManager";

// Type declarations for missing modules
declare var Alpine: any;

// Global flag to prevent multiple form initializations
let globalAddToCartFormsInitialized = false;
let globalAddToCartThrottle = false; // Global throttle for all add-to-cart operations
let globalSubsOutsideClickAttached = false; // One-shot document listener for closing dropdowns
let globalSubsDismissAttached = false; // One-shot scroll/resize listeners for closing dropdowns

class SideCart extends HTMLElement {
  private cartAPI;
  handleAddToCart: ((evt: Event) => Promise<void>) | undefined;
  private isUpdating: boolean = false;
  private listenersInitialized: boolean = false;
  private addToCartFormsInitialized: boolean = false; // NEW: Track add-to-cart forms separately
  private isRemoving: boolean = false;
  private removeDebounceTimer: number | null = null;
  private updateQueue: Map<string, number> = new Map();
  private updateDebounceTimer: number | null = null;

  constructor() {
    super();
    this.cartAPI = CartAPI;
    this.init();
  }

  private init(): void {
    this.style.setProperty("--animate-duration", ".35s");
  }

  /**
   * Opens the side cart.
   * @param triggeredBy - The source that triggered the cart to open.
   */
  open(triggeredBy: string): void {
    this.classList.add("open");
    document.body.classList.add("sidecart-open");

    const overlay = document.querySelector(".page-overlay-sidecart");
    if (overlay) {
      overlay.classList.add("open");
    }

    if (typeof Alpine !== "undefined" && Alpine.store) {
      Alpine.store("sidecart").open = true;
    }
  }

  /**
   * Closes the side cart.
   * @param triggeredBy - The source that triggered the cart to close.
   */
  close(triggeredBy: string): void {
    this.classList.remove("open");
    document.body.classList.remove("sidecart-open");

    const overlay = document.querySelector(".page-overlay-sidecart");
    if (overlay) {
      overlay.classList.remove("open");
    }

    if (typeof Alpine !== "undefined" && Alpine.store) {
      Alpine.store("sidecart").open = false;
    }
  }

  /**
   * Sets up event listeners for the side cart.
   */
  setupEventListeners(): void {
    if (this.listenersInitialized) {
      return;
    }

    this.initializeCloseEvents();
    this.initializeCartIconClick(); // Move this here since cart icon is outside SideCart
    this.initializeRemoveEvent();
    this.initializeBundleRemove();
    this.initializeUpdateEvent();
    this.initializeQuantityButtons();
    this.initializeRewardProtection();
    this.initializeSubscriptionWidgets();

    this.listenersInitialized = true;
  }


  private initializeBundleRemove(): void {
    const bundleRemoveBtn = this.querySelector('[data-bundle-remove]');

    if (!bundleRemoveBtn) {
      return;
    }

    const existingHandler = (bundleRemoveBtn as any)._bundleRemoveHandler;
    if (existingHandler) {
      bundleRemoveBtn.removeEventListener("click", existingHandler);
    }

    const newHandler = async (evt: Event) => {
      evt.preventDefault();
      evt.stopPropagation();

      if (this.isRemoving) return;
      this.isRemoving = true;

      try {
        bundleRemoveBtn.classList.add("loading");
        (bundleRemoveBtn as HTMLButtonElement).disabled = true;

        const cartRes = await fetch("/cart.js", {
          headers: { Accept: "application/json" },
        });
        const cart = await cartRes.json();

        const updates: Record<string, number> = {};

        cart.items.forEach((item: any) => {
          const isBYOB =
            item?.properties?.byob === true ||
            item?.properties?.byob === "true" ||
            item?.properties?._byob === "true";

          if (isBYOB && item.key) {
            updates[item.key] = 0;
          }
        });

        if (Object.keys(updates).length === 0) {
          this.isRemoving = false;
          return;
        }

        await fetch("/cart/update.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify({
            updates,
            sections: "ajax-side-cart",
          }),
        });

        await this.refreshSideCart();

        if (typeof (window as any).initBundleContents === "function") {
          (window as any).initBundleContents();
        }
      } catch (error) {
        console.error("❌ Error removing bundle:", error);
      } finally {
        bundleRemoveBtn.classList.remove("loading");
        (bundleRemoveBtn as HTMLButtonElement).disabled = false;
        this.isRemoving = false;
      }
    };

    (bundleRemoveBtn as any)._bundleRemoveHandler = newHandler;
    bundleRemoveBtn.addEventListener("click", newHandler);
  }

  /**
   * Initialize add-to-cart forms globally (only once)
   * This should be called separately and only once per page load
   */
  public initializeAddToCartFormsGlobally(): void {
    if (this.addToCartFormsInitialized || globalAddToCartFormsInitialized) {
      return;
    }

    this.initializeAddToCartForms();
    this.addToCartFormsInitialized = true;
    globalAddToCartFormsInitialized = true;
  }

  /**
   * Initialize new add-to-cart forms (for dynamically loaded content like quick view)
   * This can be called multiple times to initialize new forms
   */
  public initializeNewAddToCartForms(): void {
    this.initializeAddToCartForms();
  }

  /**
   * Initializes close events.
   */
  private initializeCloseEvents(): void {
    // Close button
    const closeButtons = this.querySelectorAll("#side-cart-close");
    closeButtons.forEach((closeButton) => {
      closeButton.addEventListener("click", () => this.close("close-button"));
    });

    // Overlay click
    const overlay = document.querySelector(".page-overlay-sidecart");
    if (overlay) {
      overlay.addEventListener("click", () => this.close("overlay"));
    }

    // ESC key
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.classList.contains("open")) {
        this.close("escape-key");
      }
    });
  }

  /**
   * Initializes the add-to-cart event.
   */
  public initializeAddEvent(): void {
    // This method is deprecated - use initializeAddToCartFormsGlobally() instead
    this.initializeAddToCartFormsGlobally();
  }

  /**
   * Initialize cart icon click to open side cart
   */
  public initializeCartIconClick(): void {
    const cartButtons = document.querySelectorAll("#side-cart-button, #side-cart-button-desktop");
    cartButtons.forEach((cartButton) => {
      const existingHandler = (cartButton as any)._clickHandler;
      if (existingHandler) {
        cartButton.removeEventListener("click", existingHandler);
      }

      const newHandler = (evt: Event) => {
        evt.preventDefault();
        evt.stopPropagation();
        this.open("cart-icon-click");
      };

      (cartButton as any)._clickHandler = newHandler;
      cartButton.addEventListener("click", newHandler);
    });
  }

  /**
   * Initialize add to cart form submissions
   * Only runs once per page load to prevent duplicate event listeners
   */
  private initializeAddToCartForms(): void {
    console.log("Initializing add-to-cart forms...");
    const addToCartForms = document.querySelectorAll(".ajax-add-to-cart");

    addToCartForms.forEach((form) => {
      // Check if this form already has our handler
      if ((form as any)._bullshotHandlerAttached) {
        return; // Skip if already initialized
      }

      const newHandler = async (evt: Event) => {
        evt.preventDefault();
        evt.stopImmediatePropagation(); // Prevent other handlers from running

        // Per-product in-flight guard - prevents double-submitting the SAME
        // product while its add is in progress. Different products can still
        // be added back-to-back (no global lock).
        if ((form as any)._isAddingToCart) {
          return;
        }

        // Find the submit button for visual feedback
        const button = form.querySelector(
          '.add-to-cart, [type="submit"]',
        ) as HTMLButtonElement;
        const originalText = button ? button.textContent : "";

        // Mark this form's add as in-flight
        (form as any)._isAddingToCart = true;

        // Update button visual state - Loading
        if (button) {
          button.textContent = "Adding...";
          button.disabled = true;
          button.classList.add("loading");
        }

        try {
          const formElement = form as HTMLFormElement;
          const formData = new FormData(formElement);

          // Add sections to get updated cart content
          formData.append("sections", "ajax-side-cart");

          const response = await fetch("/cart/add.js", {
            method: "POST",
            body: formData,
            headers: {
              "X-Requested-With": "XMLHttpRequest",
            },
          });

          if (response.ok) {
            // Update button to success state
            if (button) {
              button.textContent = "Added!";
              button.classList.remove("loading");
              button.classList.add("btn-success");
            }

            // Optimize: Run reward evaluation and cart refresh in parallel
            const rewardPromise = RewardManager.evaluateAndUpdateRewards();
            const refreshPromise = this.refreshSideCart();

            const [rewardsChanged] = await Promise.all([
              rewardPromise,
              refreshPromise,
            ]);

            // If rewards were changed, refresh once more to show them
            if (rewardsChanged) {
              await this.refreshSideCart();
            }

            this.open("add-to-cart");

            // Restore button after 2 seconds
            setTimeout(() => {
              if (button) {
                button.textContent = originalText;
                button.disabled = false;
                button.classList.remove("btn-success");
              }
            }, 2000);
          } else {
            const errorText = await response.text();
            console.error("Failed to add product to cart:", errorText);

            // Update button to error state
            if (button) {
              button.textContent = "Error!";
              button.classList.remove("loading");
              button.classList.add("btn-error");
            }

            alert("Error adding product to cart. Please try again.");

            // Restore button after 2 seconds
            setTimeout(() => {
              if (button) {
                button.textContent = originalText;
                button.disabled = false;
                button.classList.remove("btn-error");
              }
            }, 2000);
          }
        } catch (error) {
          console.error("Error adding product to cart:", error);

          // Update button to error state
          if (button) {
            button.textContent = "Error!";
            button.classList.remove("loading");
            button.classList.add("btn-error");
          }

          alert("Error adding product to cart. Please try again.");

          // Restore button after 2 seconds
          setTimeout(() => {
            if (button) {
              button.textContent = originalText;
              button.disabled = false;
              button.classList.remove("btn-error");
            }
          }, 2000);
        } finally {
          // Clear this form's in-flight guard once the operation completes,
          // so the same product can be added again. The button stays disabled
          // for ~2s on success (visual feedback) which prevents spam clicks.
          (form as any)._isAddingToCart = false;
        }
      };

      // Remove any existing listeners on this form
      const existingHandler = (form as any)._submitHandler;
      if (existingHandler) {
        form.removeEventListener("submit", existingHandler);
      }

      // Remove the action attribute to prevent default form submission/redirect
      const formElement = form as HTMLFormElement;
      if (formElement.action) {
        formElement.setAttribute("data-original-action", formElement.action);
        formElement.removeAttribute("action");
      }

      (form as any)._submitHandler = newHandler;
      (form as any)._bullshotHandlerAttached = true; // Mark as initialized
      // Use capture phase to ensure our handler runs before others
      form.addEventListener("submit", newHandler, {
        capture: true,
        passive: false,
      });
    });
  }

  public initializeAddToCartFormsPartial(upsells: HTMLElement): void {
    console.log("Initializing add-to-cart forms...");
    const addToCartForms = upsells.querySelectorAll(".ajax-add-to-cart");

    addToCartForms.forEach((form) => {
      // Check if this form already has our handler
      if ((form as any)._bullshotHandlerAttached) {
        return; // Skip if already initialized
      }

      const newHandler = async (evt: Event) => {
        evt.preventDefault();
        evt.stopImmediatePropagation(); // Prevent other handlers from running

        // Per-product in-flight guard - prevents double-submitting the SAME
        // product while its add is in progress. Different products can still
        // be added back-to-back (no global lock).
        if ((form as any)._isAddingToCart) {
          return;
        }

        // Find the submit button for visual feedback
        const button = form.querySelector(
          '.add-to-cart, [type="submit"]',
        ) as HTMLButtonElement;
        const originalText = button ? button.textContent : "";

        // Mark this form's add as in-flight
        (form as any)._isAddingToCart = true;

        // Update button visual state - Loading
        if (button) {
          button.textContent = "Adding...";
          button.disabled = true;
          button.classList.add("loading");
        }

        try {
          const formElement = form as HTMLFormElement;
          const formData = new FormData(formElement);

          // Add sections to get updated cart content
          formData.append("sections", "ajax-side-cart");

          const response = await fetch("/cart/add.js", {
            method: "POST",
            body: formData,
            headers: {
              "X-Requested-With": "XMLHttpRequest",
            },
          });

          if (response.ok) {
            // Update button to success state
            if (button) {
              button.textContent = "Added!";
              button.classList.remove("loading");
              button.classList.add("btn-success");
            }

            // Optimize: Run reward evaluation and cart refresh in parallel
            const rewardPromise = RewardManager.evaluateAndUpdateRewards();
            const refreshPromise = this.refreshSideCart();

            const [rewardsChanged] = await Promise.all([
              rewardPromise,
              refreshPromise,
            ]);

            // If rewards were changed, refresh once more to show them
            if (rewardsChanged) {
              await this.refreshSideCart();
            }

            this.open("add-to-cart");

            // Restore button after 2 seconds
            setTimeout(() => {
              if (button) {
                button.textContent = originalText;
                button.disabled = false;
                button.classList.remove("btn-success");
              }
            }, 2000);
          } else {
            const errorText = await response.text();
            console.error("Failed to add product to cart:", errorText);

            // Update button to error state
            if (button) {
              button.textContent = "Error!";
              button.classList.remove("loading");
              button.classList.add("btn-error");
            }

            alert("Error adding product to cart. Please try again.");

            // Restore button after 2 seconds
            setTimeout(() => {
              if (button) {
                button.textContent = originalText;
                button.disabled = false;
                button.classList.remove("btn-error");
              }
            }, 2000);
          }
        } catch (error) {
          console.error("Error adding product to cart:", error);

          // Update button to error state
          if (button) {
            button.textContent = "Error!";
            button.classList.remove("loading");
            button.classList.add("btn-error");
          }

          alert("Error adding product to cart. Please try again.");

          // Restore button after 2 seconds
          setTimeout(() => {
            if (button) {
              button.textContent = originalText;
              button.disabled = false;
              button.classList.remove("btn-error");
            }
          }, 2000);
        } finally {
          // Clear this form's in-flight guard once the operation completes,
          // so the same product can be added again. The button stays disabled
          // for ~2s on success (visual feedback) which prevents spam clicks.
          (form as any)._isAddingToCart = false;
        }
      };

      // Remove any existing listeners on this form
      const existingHandler = (form as any)._submitHandler;
      if (existingHandler) {
        form.removeEventListener("submit", existingHandler);
      }

      // Remove the action attribute to prevent default form submission/redirect
      const formElement = form as HTMLFormElement;
      if (formElement.action) {
        formElement.setAttribute("data-original-action", formElement.action);
        formElement.removeAttribute("action");
      }

      (form as any)._submitHandler = newHandler;
      (form as any)._bullshotHandlerAttached = true; // Mark as initialized
      // Use capture phase to ensure our handler runs before others
      form.addEventListener("submit", newHandler, {
        capture: true,
        passive: false,
      });
    });
  }

  /**
   * Initializes the remove-from-cart event.
   */
  private initializeRemoveEvent(): void {
    const buttons = this.querySelectorAll(".side-cart-item__remove");
    buttons.forEach((button) => {
      const existingHandler = (button as any)._removeHandler;
      if (existingHandler) {
        button.removeEventListener("click", existingHandler);
      }

      const newHandler = async (evt: Event) => {
        evt.preventDefault();
        evt.stopPropagation();

        if (this.isRemoving) return;

        if (this.removeDebounceTimer) {
          clearTimeout(this.removeDebounceTimer);
        }

        this.removeDebounceTimer = window.setTimeout(async () => {
          if (this.isRemoving) return;

          this.isRemoving = true;

          // Prefer the line's unique key over its variant ID: a variant ID
          // alone is ambiguous once two lines share it (e.g. a regular-price
          // line and a promo-discounted line for the same product, which
          // differ only by line item properties). /cart/update.js can't
          // target a keyed-by-properties line via variant ID alone.
          const container = button.closest("[data-line-key]");
          const lineKey = container?.getAttribute("data-line-key");
          const variantId = button.getAttribute("data-variant-id");
          const lineItem = button.getAttribute("data-line-item");
          if (lineKey || variantId) {
            await this.updateCartItem(lineKey || variantId!, 0);

            if (typeof window.initBundleContents === "function") {
              window.initBundleContents();
            }
          } else if (lineItem) {
            const lineNumber = parseInt(lineItem, 10);
            if (lineNumber > 0) {
              try {
                const formData = {
                  line: lineNumber,
                  quantity: 0,
                  sections: "ajax-side-cart",
                };

                const result = await this.cartAPI.removeFromCart(formData);

                // Optimize: Run reward evaluation and cart refresh in parallel
                const rewardPromise = RewardManager.evaluateAndUpdateRewards();
                const refreshPromise = this.refreshSideCart();

                const [rewardsChanged] = await Promise.all([
                  rewardPromise,
                  refreshPromise,
                ]);

                // If rewards were changed, refresh once more
                if (rewardsChanged) {
                  await this.refreshSideCart();
                }
              } catch (error) {
                console.error("Error removing item from cart:", error);
              }
            }
          }

          this.isRemoving = false;
        }, 50); // Reduced debounce for remove operations
      };

      (button as any)._removeHandler = newHandler;
      button.addEventListener("click", newHandler);
    });
  }

  /**
   * Initializes the update cart item event.
   */
  private initializeUpdateEvent(): void {
    const inputs = this.querySelectorAll(".side-cart-item__quantity-input");
    inputs.forEach((input) => {
      const inputElement = input as HTMLInputElement;

      const existingHandler = (inputElement as any)._updateHandler;
      if (existingHandler) {
        inputElement.removeEventListener("change", existingHandler);
      }

      const newHandler = (evt: Event) => {
        evt.preventDefault();

        // See initializeQuantityButtons: line key beats variant ID so a
        // promo-discounted line doesn't get confused with a regular-price
        // line for the same variant.
        const container = inputElement.closest("[data-line-key]");
        const lineKey = container?.getAttribute("data-line-key");
        const variantId = inputElement.getAttribute("data-variant-id");
        const lineItem = inputElement.getAttribute("data-line-item");
        const quantity = parseInt(inputElement.value, 10);

        if (quantity >= 0) {
          if (lineKey || variantId) {
            this.updateCartItem(lineKey || variantId!, quantity);
          } else if (lineItem) {
            this.updateCartItem(lineItem, quantity);
          }
        }
      };

      (inputElement as any)._updateHandler = newHandler;
      inputElement.addEventListener("change", newHandler);
    });
  }

  /**
   * Initializes the quantity buttons (+/-) event listeners.
   */
  private initializeQuantityButtons(): void {
    // A variant ID alone can't disambiguate two lines that share it (e.g. a
    // regular-price line and a promo-discounted line for the same product,
    // which differ only by line item properties) — scope to the same <li>
    // via its unique line key instead, both to find the right input and to
    // target the right line in the /cart/update.js call.
    const resolveLineTarget = (
      button: Element,
    ): { input: HTMLInputElement | null; itemKey: string | null } => {
      const container = button.closest("[data-line-key]");
      const input = container?.querySelector(
        ".side-cart-item__quantity-input",
      ) as HTMLInputElement | null;
      const lineKey = container?.getAttribute("data-line-key");
      const variantId = button.getAttribute("data-variant-id");
      return { input, itemKey: lineKey || variantId };
    };

    // Increase buttons
    const increaseButtons = this.querySelectorAll("[data-quantity-increase]");
    increaseButtons.forEach((button) => {
      button.addEventListener("click", (evt) => {
        evt.preventDefault();

        const { input, itemKey } = resolveLineTarget(button);

        if (input && itemKey) {
          const currentQuantity = parseInt(input.value);
          const newQuantity = currentQuantity + 1;
          input.value = newQuantity.toString();
          this.updateCartItem(itemKey, newQuantity);
        }
      });
    });

    // Decrease buttons
    const decreaseButtons = this.querySelectorAll("[data-quantity-decrease]");
    decreaseButtons.forEach((button) => {
      button.addEventListener("click", (evt) => {
        evt.preventDefault();

        const { input, itemKey } = resolveLineTarget(button);

        if (input && itemKey) {
          const currentQuantity = parseInt(input.value);
          const newQuantity = Math.max(1, currentQuantity - 1);
          input.value = newQuantity.toString();
          this.updateCartItem(itemKey, newQuantity);
        }
      });
    });
  }

  /**
   * Simple cart update method - no complex state management
   */

  private updatePromise?: Promise<void>;
  private resolveUpdate?: () => void;

  private updateCartItem(itemKey: string, quantity: number): Promise<void> {
    this.updateQueue.set(itemKey, quantity);

    // If a promise already exists, reuse it
    if (!this.updatePromise) {
      this.updatePromise = new Promise<void>((resolve) => {
        this.resolveUpdate = resolve;
      });
    }

    if (this.updateDebounceTimer) {
      clearTimeout(this.updateDebounceTimer);
    }

    this.updateDebounceTimer = window.setTimeout(async () => {
      try {
        await this.executeCartUpdate();
      } finally {
        // Resolve all awaiting callers
        this.resolveUpdate?.();
        this.updatePromise = undefined;
        this.resolveUpdate = undefined;
      }
    }, 200);

    return this.updatePromise;
  }

  /**
   * Execute cart update using Shopify's native API
   */
  private async executeCartUpdate(): Promise<void> {
    if (this.updateQueue.size === 0 || this.isUpdating) {
      return;
    }

    this.isUpdating = true;
    const updates: { [key: string]: number } = {};

    // Convert queue to updates format
    this.updateQueue.forEach((quantity, itemKey) => {
      updates[itemKey] = quantity;
    });

    try {
      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({ updates }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Cart update failed:", response.status, errorText);
        throw new Error(
          `Cart update failed: ${response.status} - ${errorText}`,
        );
      }

      const result = await response.json();

      // Clear the queue
      this.updateQueue.clear();

      // Optimize: Run reward evaluation and cart refresh in parallel when possible
      // If rewards change, we'll need a second refresh, but this prevents waiting unnecessarily
      const rewardPromise = RewardManager.evaluateAndUpdateRewards();
      const refreshPromise = this.refreshSideCart();

      const rewardsChanged = await rewardPromise;
      await refreshPromise;

      // If rewards were changed, refresh once more to show the updated rewards
      if (rewardsChanged) {
        await this.refreshSideCart();
      }
    } catch (error) {
      console.error("Error in cart update:", error);
      // On error, try legacy approach
      await this.fallbackCartUpdate();
    } finally {
      this.isUpdating = false;
    }
  }

  /**
   * Fallback cart update using legacy API
   */
  private async fallbackCartUpdate(): Promise<void> {
    try {
      const items = Array.from(this.updateQueue.entries());

      for (const [itemKey, quantity] of items) {
        const numericKey = parseInt(itemKey, 10);
        if (numericKey > 0) {
          await this.cartAPI.updateItemCart({
            line: numericKey,
            quantity: quantity,
            sections: "ajax-side-cart",
          });
        }
      }

      this.updateQueue.clear();

      // OPTIMIZED: Run reward evaluation and cart refresh in parallel
      const rewardPromise = RewardManager.evaluateAndUpdateRewards();
      const refreshPromise = this.refreshSideCart();

      const [rewardsChanged] = await Promise.all([
        rewardPromise,
        refreshPromise,
      ]);

      // If rewards were changed, refresh once more to show them
      if (rewardsChanged) {
        await this.refreshSideCart();
      }
    } catch (error) {
      console.error("Fallback cart update also failed:", error);
      await this.refreshSideCart(); // At least try to refresh
    }
  }

  /**
   * Refresh the side cart content
   */
  private async refreshSideCart(): Promise<void> {
    try {
      const [sectionsRes, cartResponse] = await Promise.all([
        fetch("/?sections=ajax-side-cart"),
        fetch("/cart.js"),
      ]);

      const freshSections = await sectionsRes.json();
      const cartData = await cartResponse.json();

      this.renderHTML(freshSections, cartData.item_count);
    } catch (error) {
      console.error("Error refreshing side cart:", error);
    }
  }

  /**
   * ULTRA-OPTIMIZED: Combined reward evaluation and cart refresh
   * Uses shared cart data to minimize HTTP calls
   * @returns true if any changes were made to the cart
   */
  private async evaluateRewardsAndRefresh(): Promise<boolean> {
    try {
      // Fetch cart data once for both operations
      const [sectionsRes, cartResponse] = await Promise.all([
        fetch("/?sections=ajax-side-cart"),
        fetch("/cart.js"),
      ]);

      const freshSections = await sectionsRes.json();
      const cartData = await cartResponse.json();

      // Evaluate rewards using the fetched cart data
      const rewardsChanged = await RewardManager.evaluateAndUpdateRewards();

      // Always refresh the cart UI
      this.renderHTML(freshSections, cartData.item_count);

      return rewardsChanged;
    } catch (error) {
      console.error("Error in combined reward evaluation and refresh:", error);
      // Fallback to separate operations
      const rewardsChanged = await RewardManager.evaluateAndUpdateRewards();
      await this.refreshSideCart();
      return rewardsChanged;
    }
  }

  /**
   * Get cart item count
   */
  private async getCartItemCount(): Promise<number> {
    try {
      const response = await fetch("/cart.js");
      const cart = await response.json();
      return cart.item_count || 0;
    } catch (error) {
      console.error("Error getting cart count:", error);
      return 0;
    }
  }

  /**
   * Render HTML sections
   */
  private renderHTML(sections: any, cartItemCount: number): void {
    if (sections && sections["ajax-side-cart"]) {
      const newHTML = sections["ajax-side-cart"];
      const parser = new DOMParser();
      const newDoc = parser.parseFromString(newHTML, "text/html");

      // Preserve scroll so cart refreshes (e.g. frequency change) don't jump to top
      const contentEl = this.querySelector<HTMLElement>(".side-cart__content");
      const itemsEl = this.querySelector<HTMLElement>(".side-cart__items");
      const savedContentScroll = contentEl?.scrollTop ?? 0;
      const savedItemsScroll = itemsEl?.scrollTop ?? 0;

      // Update the side cart content directly
      const currentClasses = Array.from(this.classList);
      this.innerHTML = newHTML;

      // Restore classes
      currentClasses.forEach((cls) => this.classList.add(cls));

      // Reinitialize event listeners for the updated content
      this.listenersInitialized = false;
      this.setupEventListeners();

      // this.innerHTML replaced every node above, so any .ajax-add-to-cart
      // form (promo upsell, "You may also like") lost its bound submit
      // handler along with it. Without this, the form falls back to a plain
      // HTML submit straight to /cart/add, which redirects to /cart instead
      // of adding via fetch.
      this.initializeNewAddToCartForms();

      // Restore scroll after layout so the new nodes have measurable overflow
      requestAnimationFrame(() => {
        const newContent = this.querySelector<HTMLElement>(".side-cart__content");
        const newItems = this.querySelector<HTMLElement>(".side-cart__items");
        if (newContent && savedContentScroll > 0) {
          newContent.scrollTop = savedContentScroll;
        }
        if (newItems && savedItemsScroll > 0) {
          newItems.scrollTop = savedItemsScroll;
        }
      });

      // Notify feature flags / A/B tests to re-apply (e.g. free shipping callout)
      document.dispatchEvent(new CustomEvent("sideCart:refreshed"));
    }

    // Update cart icon counter manually
    this.updateCartIconCounter(cartItemCount);
  }

  /**
   * Update cart icon counter
   */
  private updateCartIconCounter(itemCount: number): void {
    const cartButtons = document.querySelectorAll("#side-cart-button, #side-cart-button-desktop");
    cartButtons.forEach((cartButton) => {
      const counter = cartButton.querySelector(".counter");
      if (counter) {
        counter.textContent = itemCount.toString();
      }
    });

    // Reinitialize cart icon click handler in case DOM was updated
    setTimeout(() => {
      this.initializeCartIconClick();
    }, 10);
  }

  /**
   * Initializes the "Upgrade and Save" widgets in the side cart.
   * Handles CTA click, dropdown toggle, and plan selection via /cart/change.js.
   *
   * The widget is rendered as a sibling of <li class="side-cart-item"> inside
   * the items <ul>, and is bound to its line item via the shared
   * `data-line-key` / `data-related-line-item` / `data-variant-id` attributes.
   *
   * Idempotent: re-running this on the same DOM (after a sidecart refresh)
   * does NOT duplicate listeners — each widget element is tagged with
   * `_subsWidgetBound` once its handlers are attached.
   */
  private initializeSubscriptionWidgets(): void {
    const widgets = this.querySelectorAll<HTMLElement>("[data-subscription-widget]");

    widgets.forEach((widget) => {
      if ((widget as any)._subsWidgetBound) return;
      (widget as any)._subsWidgetBound = true;

      const lineKey = widget.getAttribute("data-line-key") || "";
      const ctas = widget.querySelectorAll<HTMLButtonElement>("[data-subscription-cta]");
      const hero = widget.querySelector<HTMLElement>("[data-subscription-hero]");
      const selector = widget.querySelector<HTMLElement>("[data-subscription-selector]");
      const toggle = widget.querySelector<HTMLButtonElement>("[data-subscription-toggle]");
      const optionsList = widget.querySelector<HTMLElement>("[data-subscription-options]");
      const planNameEl = widget.querySelector<HTMLElement>("[data-current-plan-name]");

      // In-flight lock so rapid clicks / double-fires can't race the cart API.
      let busy = false;

      const closeDropdown = () => {
        optionsList?.classList.remove("sidecart-subscription-upgrade__options--open");
        toggle?.setAttribute("aria-expanded", "false");
        // Clear the inline coordinates set while open so a stale position can't
        // leak into the next open (see positionDropdown).
        if (optionsList) {
          optionsList.style.top = "";
          optionsList.style.left = "";
          optionsList.style.width = "";
        }
      };

      // Position the fixed-overlay dropdown relative to the side-cart element.
      // `side-cart` carries a transform, so it is the containing block for the
      // fixed options list — coordinates are computed relative to its rect
      // (not the raw viewport). Flips above the toggle when there isn't enough
      // room below it before the footer.
      const positionDropdown = () => {
        if (!toggle || !optionsList) return;

        const sideCartRect = this.getBoundingClientRect();
        const toggleRect = toggle.getBoundingClientRect();
        const footer = this.querySelector<HTMLElement>(".side-cart__footer");
        const gap = 2;

        optionsList.style.width = `${toggleRect.width}px`;
        optionsList.style.left = `${toggleRect.left - sideCartRect.left}px`;

        // Measure the dropdown height (it is display:block once --open).
        const optionsHeight = optionsList.offsetHeight;
        const boundaryBottom = footer
          ? footer.getBoundingClientRect().top
          : sideCartRect.bottom;
        const spaceBelow = boundaryBottom - toggleRect.bottom;

        if (spaceBelow < optionsHeight + gap && toggleRect.top - sideCartRect.top > optionsHeight + gap) {
          // Flip above the toggle.
          optionsList.style.top = `${toggleRect.top - sideCartRect.top - optionsHeight - gap}px`;
        } else {
          optionsList.style.top = `${toggleRect.bottom - sideCartRect.top + gap}px`;
        }
      };

      const labelFor = (option: HTMLElement): string =>
        option.getAttribute("data-plan-label")?.trim() ||
        option.textContent?.trim() ||
        "";

      // ── CTA: upgrade to the first available selling plan ─────────────────
      // The hero widget (outside the <li>) carries data-first-plan-id and
      // data-first-plan-label so it can upgrade without needing an inline
      // options list (those live on the inline selector widget, inside the
      // <li>). When both exist on the same widget we still honour the
      // options list as a fallback.
      ctas.forEach((cta) => {
        cta.addEventListener("click", async (evt) => {
          evt.preventDefault();
          if (busy || !lineKey) return;

          let firstPlanId = parseInt(
            widget.getAttribute("data-first-plan-id") || "0",
            10,
          );
          let firstPlanLabel =
            widget.getAttribute("data-first-plan-label")?.trim() || "";

          if (!firstPlanId) {
            const firstPlanOption = optionsList?.querySelector<HTMLElement>(
              "[data-plan-id]:not([data-plan-id=''])",
            );
            if (firstPlanOption) {
              firstPlanId = parseInt(
                firstPlanOption.getAttribute("data-plan-id") || "0",
                10,
              );
              firstPlanLabel = labelFor(firstPlanOption);
            }
          }

          if (!firstPlanId) return;

          busy = true;
          cta.setAttribute("aria-disabled", "true");

          // Optimistic UI: swap hero → selector right away. The refresh from
          // the server-rendered cart overwrites this DOM, so it's only for
          // the brief window between request and refresh.
          hero?.classList.add("sidecart-subscription-upgrade__hero--hidden");
          selector?.classList.remove("sidecart-subscription-upgrade__selector--hidden");
          if (planNameEl && firstPlanLabel) planNameEl.textContent = firstPlanLabel;

          if (optionsList) {
            optionsList
              .querySelectorAll<HTMLElement>("[data-plan-id]")
              .forEach((o) => {
                if (parseInt(o.getAttribute("data-plan-id") || "0", 10) === firstPlanId) {
                  o.classList.add("sidecart-subscription-upgrade__option--active");
                } else {
                  o.classList.remove("sidecart-subscription-upgrade__option--active");
                }
              });
          }

          try {
            const res = await fetch("/cart/change.js", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "X-Requested-With": "XMLHttpRequest",
              },
              body: JSON.stringify({ id: lineKey, selling_plan: firstPlanId }),
            });

            if (!res.ok) {
              console.error("Failed to apply subscription plan:", await res.text());
              return;
            }

            await this.refreshSideCart();
          } catch (error) {
            console.error("Error applying subscription plan:", error);
          } finally {
            busy = false;
            cta.removeAttribute("aria-disabled");
          }
        });
      });

      // ── Selector toggle: open / close the options dropdown ───────────────
      if (toggle) {
        toggle.addEventListener("click", (evt) => {
          evt.preventDefault();
          const isOpen = optionsList?.classList.contains(
            "sidecart-subscription-upgrade__options--open",
          );
          if (isOpen) {
            closeDropdown();
          } else {
            optionsList?.classList.add(
              "sidecart-subscription-upgrade__options--open",
            );
            toggle.setAttribute("aria-expanded", "true");
            positionDropdown();
          }
        });

        toggle.addEventListener("keydown", (evt) => {
          if (evt.key === "Escape") {
            closeDropdown();
            toggle.focus();
          }
        });
      }

      // ── Option click: switch plan or revert to OTP ───────────────────────
      if (optionsList) {
        const options = optionsList.querySelectorAll<HTMLElement>("[data-plan-id]");

        const handleOptionSelect = async (option: HTMLElement) => {
          if (busy || !lineKey) return;

          const planIdRaw = option.getAttribute("data-plan-id") || "";
          const planId = planIdRaw === "" ? 0 : parseInt(planIdRaw, 10);
          const variantId = widget.getAttribute("data-variant-id") || "";

          closeDropdown();

          options.forEach((o) =>
            o.classList.remove("sidecart-subscription-upgrade__option--active"),
          );
          option.classList.add("sidecart-subscription-upgrade__option--active");

          busy = true;

          try {
            if (planId === 0) {
              // Revert to OTP. Shopify treats subscription and one-time as
              // different line items, so we remove the subscription line then
              // re-add the variant with no selling_plan.
              const quantityInput = this.querySelector<HTMLInputElement>(
                `input.side-cart-item__quantity-input[data-variant-id="${variantId}"]`,
              );
              const quantity = quantityInput
                ? Math.max(1, parseInt(quantityInput.value, 10) || 1)
                : 1;

              const removeRes = await fetch("/cart/change.js", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  "X-Requested-With": "XMLHttpRequest",
                },
                body: JSON.stringify({ id: lineKey, quantity: 0 }),
              });

              if (!removeRes.ok) {
                console.error(
                  "Failed to remove subscription item:",
                  await removeRes.text(),
                );
                return;
              }

              const addRes = await fetch("/cart/add.js", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  "X-Requested-With": "XMLHttpRequest",
                },
                body: JSON.stringify({ id: parseInt(variantId, 10), quantity }),
              });

              if (!addRes.ok) {
                console.error(
                  "Failed to re-add item as one-time:",
                  await addRes.text(),
                );
                return;
              }
            } else {
              // Switch between selling plans on the existing line item.
              if (planNameEl) planNameEl.textContent = labelFor(option);

              const changeRes = await fetch("/cart/change.js", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  "X-Requested-With": "XMLHttpRequest",
                },
                body: JSON.stringify({ id: lineKey, selling_plan: planId }),
              });

              if (!changeRes.ok) {
                console.error(
                  "Failed to update selling plan:",
                  await changeRes.text(),
                );
                return;
              }
            }

            await this.refreshSideCart();
          } catch (error) {
            console.error("Error updating selling plan:", error);
          } finally {
            busy = false;
          }
        };

        options.forEach((option) => {
          option.addEventListener("click", (evt) => {
            evt.preventDefault();
            handleOptionSelect(option);
          });
          option.addEventListener("keydown", (evt) => {
            if (evt.key === "Enter" || evt.key === " ") {
              evt.preventDefault();
              handleOptionSelect(option);
            } else if (evt.key === "Escape") {
              closeDropdown();
              toggle?.focus();
            }
          });
        });
      }

    });

    // ── Outside click: close any open dropdown ───────────────────────────────
    // Attached on `document` exactly once per page load. On each click we walk
    // every widget currently mounted inside this side cart and close those the
    // click landed outside of. This keeps the listener count bounded across the
    // many refreshes triggered by quantity/add/remove operations.
    if (!globalSubsOutsideClickAttached) {
      globalSubsOutsideClickAttached = true;
      document.addEventListener("click", (e) => {
        const sideCart = document.querySelector("side-cart");
        if (!sideCart) return;
        sideCart
          .querySelectorAll<HTMLElement>("[data-subscription-widget]")
          .forEach((w) => {
            if (w.contains(e.target as Node)) return;
            const list = w.querySelector<HTMLElement>("[data-subscription-options]");
            const tgl = w.querySelector<HTMLButtonElement>("[data-subscription-toggle]");
            SideCart.dismissDropdown(list, tgl);
          });
      });
    }

    // ── Scroll / resize: close any open dropdown ─────────────────────────────
    // The dropdown is a fixed overlay positioned relative to the (fixed)
    // side-cart, so scrolling the items list would otherwise leave it detached
    // from its toggle. Closing on scroll/resize keeps it anchored. Attached on
    // the scroll container + window exactly once per page load.
    if (!globalSubsDismissAttached) {
      globalSubsDismissAttached = true;

      const closeAllDropdowns = () => {
        const sideCart = document.querySelector("side-cart");
        if (!sideCart) return;
        sideCart
          .querySelectorAll<HTMLElement>("[data-subscription-widget]")
          .forEach((w) => {
            const list = w.querySelector<HTMLElement>("[data-subscription-options]");
            if (!list?.classList.contains("sidecart-subscription-upgrade__options--open")) {
              return;
            }
            const tgl = w.querySelector<HTMLButtonElement>("[data-subscription-toggle]");
            SideCart.dismissDropdown(list, tgl);
          });
      };

      // Capture phase so it fires for the scrollable inner container even
      // though scroll doesn't bubble.
      document.addEventListener("scroll", closeAllDropdowns, true);
      window.addEventListener("resize", closeAllDropdowns);
    }
  }

  /**
   * Closes a subscription frequency dropdown and clears the inline coordinates
   * applied by `positionDropdown`. Shared by the outside-click, scroll and
   * resize dismissers so no stale position leaks into the next open.
   */
  private static dismissDropdown(
    list: HTMLElement | null,
    toggle: HTMLButtonElement | null,
  ): void {
    if (!list) return;
    list.classList.remove("sidecart-subscription-upgrade__options--open");
    list.style.top = "";
    list.style.left = "";
    list.style.width = "";
    toggle?.setAttribute("aria-expanded", "false");
  }

  /**
   * Initialize reward protection
   * Prevents users from manually changing quantities of reward products
   */
  public initializeRewardProtection(): void {
    // Find all reward products and protect their quantity controls
    const rewardItems = this.querySelectorAll(".side-cart-item--reward");

    rewardItems.forEach((item) => {
      // Disable quantity input for reward products
      const quantityInput = item.querySelector(
        ".side-cart-item__quantity-input",
      ) as HTMLInputElement;
      if (quantityInput) {
        quantityInput.setAttribute("readonly", "true");
        quantityInput.style.pointerEvents = "none";
        quantityInput.style.opacity = "0.6";
      }

      // Disable quantity increase/decrease buttons
      const increaseBtn = item.querySelector(
        "[data-quantity-increase]",
      ) as HTMLButtonElement;
      const decreaseBtn = item.querySelector(
        "[data-quantity-decrease]",
      ) as HTMLButtonElement;

      if (increaseBtn) {
        increaseBtn.disabled = true;
        increaseBtn.style.opacity = "0.6";
        increaseBtn.style.pointerEvents = "none";
      }

      if (decreaseBtn) {
        decreaseBtn.disabled = true;
        decreaseBtn.style.opacity = "0.6";
        decreaseBtn.style.pointerEvents = "none";
      }
    });
  }
}

// Register the custom element ONLY if not already defined
if (!customElements.get("side-cart")) {
  customElements.define("side-cart", SideCart);
}

/**
 * Function to initialize SideCart
 */
export function initSideCart(): void {
  if (!customElements.get("side-cart")) {
    customElements.define("side-cart", SideCart);
  }

  const setupSideCart = () => {
    const sideCartElement = document.querySelector("side-cart") as SideCart;
    if (sideCartElement) {
      (window as any).sideCartInstance = sideCartElement;

      if (typeof sideCartElement.setupEventListeners === "function") {
        sideCartElement.setupEventListeners();
      }

      // Initialize add-to-cart forms globally (only once)
      if (
        typeof sideCartElement.initializeAddToCartFormsGlobally === "function"
      ) {
        sideCartElement.initializeAddToCartFormsGlobally();
      }

      return true;
    } else {
      return false;
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      customElements
        .whenDefined("side-cart")
        .then(setupSideCart)
        .catch((error) => {
          console.error("Error waiting for side-cart definition:", error);
        });
    });
  } else {
    customElements
      .whenDefined("side-cart")
      .then(() => {
        if (!setupSideCart()) {
          setTimeout(setupSideCart, 100);
        }
      })
      .catch((error) => {
        console.error("Error waiting for side-cart definition:", error);
      });
  }

  document.addEventListener("sideCart:reinitialize", () => {
    customElements.whenDefined("side-cart").then(() => {
      const sideCartElement = document.querySelector("side-cart") as SideCart;
      if (
        sideCartElement &&
        typeof sideCartElement.setupEventListeners === "function"
      ) {
        (sideCartElement as any).listenersInitialized = false;
        sideCartElement.setupEventListeners();
      }
    });
  });
}

export default SideCart;
